import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, take, timeout } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';
import { DEV_JWT_KEY } from './dev-auth.constants';

/**
 * Auth-Guard der prueft ob der User eingeloggt ist (MSAL oder Supabase).
 *
 * Wartet zuerst bis MSAL den Redirect verarbeitet hat, damit nach dem
 * Azure-AD-Login nicht faelschlich zur Login-Seite weitergeleitet wird.
 *
 * Falls nicht eingeloggt → Weiterleitung zu /login.
 * Kein automatischer Redirect zu Azure AD – der User soll selbst waehlen
 * wie er sich anmeldet (Google, Meta, Microsoft, E-Mail/Passwort).
 *
 * Ersetzt den MsalGuard auf allen geschuetzten Routes.
 */
export const authGuard: CanActivateFn = async () => {
  // Dev-JWT in non-Production als gueltiges Auth-Token akzeptieren
  if (!environment.production && localStorage.getItem(DEV_JWT_KEY)) {
    return true;
  }

  const authService = inject(AuthService);
  const router = inject(Router);
  const broadcastService = inject(MsalBroadcastService);

  // Warten bis MSAL keine Interaktion mehr laeuft (Redirect-Verarbeitung abgeschlossen).
  // Ohne dieses Warten wird der User nach dem Azure-AD-Login faelschlich zu /login
  // geleitet, weil getAllAccounts() noch leer ist waehrend MSAL den Hash verarbeitet.
  try {
    await firstValueFrom(
      broadcastService.inProgress$.pipe(
        filter(status => status === InteractionStatus.None),
        take(1),
        timeout(3000)
      )
    );
  } catch {
    // Timeout: MSAL haengt – weiter ohne Warten (Supabase-Auth greift)
  }

  if (authService.isLoggedIn()) {
    return true;
  }

  router.navigate(['/login']);
  return false;
};
